import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Handshake, ArrowCounterClockwise } from '@phosphor-icons/react';
import { bihinApi } from './bihinApi';
import { formatDate as fmtLoanDate } from '@/utils/datetime';

export default function BihinLoans() {
  const [tab, setTab] = useState('active');
  const [loans, setLoans] = useState([]);

  const load = useCallback(() => {
    bihinApi.loans(tab === 'active' ? { active: 1 } : {}).then(setLoans).catch(() => {});
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const doReturn = async (l) => {
    try {
      await bihinApi.returnLoan(l.id);
      toast.success('返却しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '返却に失敗しました');
    }
  };

  return (
    <div className="space-y-4" data-testid="bihin-loans-page">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900">貸出管理</h1>
        <p className="text-xs text-zinc-500 mt-0.5">備品の貸出状況と履歴です。返却は誰でも記録できます。</p>
      </div>

      <div className="flex gap-1">
        {[['active', '貸出中'], ['all', '履歴']].map(([k, label]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              tab === k ? 'bg-teal-700 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}
            data-testid={`bihin-loans-tab-${k}`}>
            {label}
          </button>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        {loans.length === 0 ? (
          <div className="py-12 text-center text-zinc-400 text-sm">
            <Handshake size={28} weight="duotone" className="mx-auto mb-2 text-zinc-300" />
            {tab === 'active' ? '貸出中の備品はありません' : '貸出履歴はありません'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-zinc-500 border-b border-zinc-200 bg-zinc-50/60">
                <tr>
                  <th className="text-left py-2.5 px-3">備品</th>
                  <th className="text-left py-2.5 px-3">借りている人</th>
                  <th className="text-left py-2.5 px-3">貸出日</th>
                  <th className="text-left py-2.5 px-3">返却期限</th>
                  <th className="text-left py-2.5 px-3">状態</th>
                  <th className="py-2.5 px-3"></th>
                </tr>
              </thead>
              <tbody>
                {loans.map((l) => (
                  <tr key={l.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`bihin-loan-row-${l.id}`}>
                    <td className="py-2 px-3">
                      <div className="font-semibold text-zinc-800"><span data-no-translate>{l.item_name}</span></div>
                      {l.item_asset_no && <div className="text-[10px] font-mono text-zinc-400">{l.item_asset_no}</div>}
                    </td>
                    <td className="py-2 px-3 text-zinc-700">{l.borrower_name}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500">{fmtLoanDate(l.loaned_at)}</td>
                    <td className="py-2 px-3 text-xs text-zinc-500">{l.due_date || '—'}</td>
                    <td className="py-2 px-3">
                      {l.returned_at ? (
                        <Badge variant="outline" className="bg-zinc-100 text-zinc-500 border-zinc-200">返却済み</Badge>
                      ) : l.overdue ? (
                        <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200">延滞中</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">貸出中</Badge>
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">
                      {!l.returned_at && (
                        <Button size="sm" variant="outline" className="h-8" onClick={() => doReturn(l)} data-testid={`bihin-loan-return-${l.id}`}>
                          <ArrowCounterClockwise size={13} className="mr-1" />返却
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
